import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Separator, Spinner } from '@1trading/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { validate as isUuid } from 'uuid';
import { DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { CopyButton } from '@/components/ui/copy-button';
import { CUSTOMER_REFERENCES_QUERY_KEY, useCustomerReferences } from '@/hooks/queries/funds/use-customer-references';
import { getErrorMessage } from '@/lib/api/error';
import { addCustomerReference, type CustomerReference } from '@/services/api/customer-references';

// The list arrives last-created-first from the server — column sorting is disabled
// everywhere so the promised order is never silently reshuffled.
const COLUMNS: ColumnDef<CustomerReference, unknown>[] = [
  {
    accessorKey: 'account_holder_id',
    header: 'PID',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-1 font-mono text-xs">
        {row.original.account_holder_id}
        <CopyButton label={`Copy ${row.original.account_holder_id}`} value={row.original.account_holder_id} />
      </span>
    ),
  },
  {
    accessorKey: 'account_holder_name',
    header: 'Customer name',
    enableSorting: false,
  },
  {
    accessorKey: 'segment',
    header: 'Segment',
    enableSorting: false,
  },
  {
    accessorKey: 'reference',
    header: 'Deposit reference',
    enableSorting: false,
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.reference}</span>,
  },
  {
    accessorKey: 'generated_at',
    header: 'Reference created at',
    enableSorting: false,
    // Deliberate change from legacy: formatDate instead of a raw substring of the timestamp
    cell: ({ row }) => <DateCell value={row.original.generated_at} />,
  },
];

const filterReferences = (references: CustomerReference[], filter: string): CustomerReference[] => {
  const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return references;
  return references.filter((row) => {
    const rowText = Object.values(row).join(' ').toLowerCase();
    return words.every((word) => rowText.includes(word));
  });
};

const UserReferencesPage = () => {
  const [filter, setFilter] = useState('');
  const [pid, setPid] = useState('');
  const [pidError, setPidError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { data: references, isPending, isError, error, refetch } = useCustomerReferences();

  // Deliberate fixes over legacy: PID validated before submit, pending state
  // disables the button, and both {success:false} bodies and thrown errors toast.
  const createReference = useMutation({
    mutationFn: addCustomerReference,
    onSuccess: async (result) => {
      if (!result.success) {
        toast.error(result.error || 'Failed to create reference');
        return;
      }
      toast.success('Reference created');
      setPid('');
      await queryClient.invalidateQueries({ queryKey: CUSTOMER_REFERENCES_QUERY_KEY });
    },
    onError: (mutationError) => toast.error(getErrorMessage(mutationError)),
  });

  const filtered = useMemo(() => filterReferences(references ?? [], filter), [references, filter]);

  const handleCreate = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = pid.trim();
    if (!isUuid(trimmed)) {
      setPidError('PID must be a valid UUID');
      return;
    }
    setPidError(null);
    createReference.mutate(trimmed);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>User References</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isError ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-red-500">Failed to load customer references: {getErrorMessage(error)}</p>
            <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
              Retry
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">{references?.length ?? 0}</strong> customer references in total, last created first
            </p>
            <div className="flex max-w-md flex-col gap-1.5">
              <Label htmlFor="reference-filter">Filter by</Label>
              <Input id="reference-filter" onChange={(event) => setFilter(event.target.value)} placeholder="Filter by any column…" value={filter} />
            </div>
            <DataTable columns={COLUMNS} data={filtered} emptyMessage="No customer references" isLoading={isPending} surface={false} />
          </>
        )}

        <Separator />

        <form className="flex max-w-xl flex-col gap-3 rounded-md border bg-muted/50 p-4" onSubmit={handleCreate}>
          <h2 className="text-sm font-semibold">Create missing reference for existing user</h2>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reference-pid">PID</Label>
            <Input
              id="reference-pid"
              onChange={(event) => {
                setPid(event.target.value);
                setPidError(null);
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
              value={pid}
            />
            {pidError ? <p className="text-sm text-red-500">{pidError}</p> : null}
          </div>
          <Button className="gap-2 self-start" disabled={createReference.isPending} type="submit" variant="primary">
            {createReference.isPending ? <Spinner className="size-4" /> : null}
            Create Reference
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
UserReferencesPage.displayName = 'UserReferencesPage';

export default UserReferencesPage;
